import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  CheckCircle2, ArrowRight, User, FileText, MessageSquare,
  Shield, Sparkles, Loader2, ChevronRight, BookOpen
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const API_URL = window.location.origin;

const CoachOnboardingModal = ({ isOpen, onComplete }) => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [termsContent, setTermsContent] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchOnboardingStatus();
      fetchTermsContent();
    }
  }, [isOpen]);

  const fetchOnboardingStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/coach/onboarding-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOnboardingStatus(response.data);
      
      // If terms already accepted, skip to step 2
      if (response.data.terms_accepted) {
        setTermsAccepted(true);
        setStep(2);
      }
    } catch (error) {
      console.error('Error fetching onboarding status:', error);
    }
  };

  const fetchTermsContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/coach/terms-content`);
      setTermsContent(response.data.content);
    } catch (error) {
      console.error('Error fetching terms:', error);
    }
  };

  const handleAcceptTerms = async () => {
    if (!termsAccepted) {
      toast.error('Please accept the terms and conditions to continue');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/coach/accept-terms`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Terms accepted!');
      setStep(2);
    } catch (error) {
      toast.error('Failed to accept terms');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteOnboarding = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/coach/complete-onboarding`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Welcome to the Coach Portal!');
      if (refreshUser) await refreshUser();
      if (onComplete) onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const totalSteps = 3;
  const progress = (step / totalSteps) * 100;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-hidden bg-white shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-forest to-forest/80 p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-white/20 rounded-lg">
              <Sparkles className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-heading">Welcome to the Coach Portal</h1>
              <p className="text-white/80 text-sm">Let's get you set up for success</p>
            </div>
          </div>
          <Progress value={progress} className="h-2 bg-white/20 mt-4" />
          <p className="text-xs text-white/60 mt-2">Step {step} of {totalSteps}</p>
        </div>

        <CardContent className="p-0">
          {/* Step 1: Welcome & Terms */}
          {step === 1 && (
            <div className="p-6">
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-forest mb-2">Coach Portal Overview</h2>
                <p className="text-gray-600">
                  Welcome, {onboardingStatus?.coach_name || user?.full_name}! This portal is your central hub for managing 
                  your coaching practice with Volition Enterprises.
                </p>
              </div>

              {/* Quick Overview Cards */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-sand/30 p-4 rounded-lg text-center">
                  <User className="h-8 w-8 text-forest mx-auto mb-2" />
                  <p className="text-sm font-medium text-forest">Manage Clients</p>
                  <p className="text-xs text-gray-500">Track sessions & progress</p>
                </div>
                <div className="bg-sand/30 p-4 rounded-lg text-center">
                  <FileText className="h-8 w-8 text-forest mx-auto mb-2" />
                  <p className="text-sm font-medium text-forest">Agreements</p>
                  <p className="text-xs text-gray-500">View & sign contracts</p>
                </div>
                <div className="bg-sand/30 p-4 rounded-lg text-center">
                  <MessageSquare className="h-8 w-8 text-forest mx-auto mb-2" />
                  <p className="text-sm font-medium text-forest">Messages</p>
                  <p className="text-xs text-gray-500">Connect with admin</p>
                </div>
              </div>

              {/* Terms & Conditions */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b flex items-center gap-2">
                  <Shield className="h-4 w-4 text-forest" />
                  <span className="font-medium text-sm">Terms & Conditions</span>
                </div>
                <ScrollArea className="h-48 p-4">
                  <div className="prose prose-sm max-w-none text-gray-600">
                    <ReactMarkdown>{termsContent}</ReactMarkdown>
                  </div>
                </ScrollArea>
              </div>

              <div className="flex items-start gap-3 mt-4 p-3 bg-sand/20 rounded-lg">
                <Checkbox 
                  id="accept-terms"
                  checked={termsAccepted}
                  onCheckedChange={setTermsAccepted}
                  className="mt-0.5"
                />
                <Label htmlFor="accept-terms" className="text-sm text-gray-700 cursor-pointer">
                  I have read and agree to the Volition Enterprises Coach Terms & Conditions
                </Label>
              </div>

              <Button 
                onClick={handleAcceptTerms}
                disabled={!termsAccepted || loading}
                className="w-full mt-4 bg-forest hover:bg-forest/90"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</>
                ) : (
                  <>Accept & Continue <ArrowRight className="ml-2 h-4 w-4" /></>
                )}
              </Button>
            </div>
          )}

          {/* Step 2: First Actions */}
          {step === 2 && (
            <div className="p-6">
              <div className="text-center mb-6">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <h2 className="text-xl font-semibold text-forest mb-2">Great! You're Almost Ready</h2>
                <p className="text-gray-600">
                  Here are your first 3 actions to get started:
                </p>
              </div>

              <div className="space-y-4">
                {/* Action 1 */}
                <div className="flex items-start gap-4 p-4 border rounded-lg hover:border-forest/50 transition-colors">
                  <div className="flex-shrink-0 w-8 h-8 bg-forest text-white rounded-full flex items-center justify-center font-bold">
                    1
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-forest flex items-center gap-2">
                      <User className="h-4 w-4" /> Review Your Profile
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {onboardingStatus?.is_new_coach 
                        ? "Complete your coach profile with your bio, specialties, and Calendly link."
                        : "We've uploaded your existing profile. Please verify all information is accurate."}
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={() => {
                        handleCompleteOnboarding();
                        navigate('/coach');
                      }}
                    >
                      Go to Profile <ChevronRight className="ml-1 h-3 w-3" />
                    </Button>
                  </div>
                </div>

                {/* Action 2 */}
                <div className="flex items-start gap-4 p-4 border rounded-lg hover:border-forest/50 transition-colors">
                  <div className="flex-shrink-0 w-8 h-8 bg-forest text-white rounded-full flex items-center justify-center font-bold">
                    2
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-forest flex items-center gap-2">
                      <FileText className="h-4 w-4" /> Check Pending Agreements
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {onboardingStatus?.pending_agreements > 0 
                        ? `You have ${onboardingStatus.pending_agreements} agreement(s) awaiting your review and signature.`
                        : "View and manage your coaching agreements in one place."}
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={() => {
                        handleCompleteOnboarding();
                        navigate('/coach/agreements');
                      }}
                    >
                      View Agreements <ChevronRight className="ml-1 h-3 w-3" />
                    </Button>
                  </div>
                </div>

                {/* Action 3 */}
                <div className="flex items-start gap-4 p-4 border rounded-lg hover:border-forest/50 transition-colors">
                  <div className="flex-shrink-0 w-8 h-8 bg-forest text-white rounded-full flex items-center justify-center font-bold">
                    3
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-forest flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" /> Say Hello to the Team
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      If you have any questions or need support, send a message to the Volition team directly through the portal.
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={() => {
                        handleCompleteOnboarding();
                        navigate('/coach/messages');
                      }}
                    >
                      Open Messages <ChevronRight className="ml-1 h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button 
                  variant="outline"
                  onClick={() => setStep(3)}
                  className="flex-1"
                >
                  Skip for Now
                </Button>
                <Button 
                  onClick={() => setStep(3)}
                  className="flex-1 bg-forest hover:bg-forest/90"
                >
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && (
            <div className="p-6 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-forest mb-2">You're All Set!</h2>
              <p className="text-gray-600 mb-6">
                Welcome to the Volition Coach Portal. You can access all features from your dashboard.
              </p>

              <div className="bg-sand/30 rounded-lg p-4 mb-6 text-left">
                <h3 className="font-medium text-forest mb-2 flex items-center gap-2">
                  <BookOpen className="h-4 w-4" /> Quick Tips
                </h3>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Access the sidebar to navigate between Clients, Agreements, and Messages</li>
                  <li>• Click the bell icon to view notifications</li>
                  <li>• Update your profile photo and bio anytime from the Profile tab</li>
                  <li>• Submit invoices directly through the Invoices section</li>
                </ul>
              </div>

              <Button 
                onClick={handleCompleteOnboarding}
                disabled={loading}
                className="w-full bg-forest hover:bg-forest/90"
                size="lg"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Starting...</>
                ) : (
                  <>Enter Coach Portal <ArrowRight className="ml-2 h-4 w-4" /></>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CoachOnboardingModal;
